import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FileText, Save, RotateCcw } from 'lucide-react';
import { clientDocuments } from '../utils/api';
import useSensitiveDataAck from '../hooks/useSensitiveDataAck';
import SensitiveDataDisclaimerModal from './SensitiveDataDisclaimerModal';

/**
 * ClientDocumentsSettingsEditor
 * ──────────────────────────────
 * Admin panel for the five client-document feature toggles plus the
 * per-type client-upload allowlist (roadmap item #14 Phase 1).
 *
 * Uses the dedicated /api/client-documents/settings endpoint rather
 * than the generic business-info one because the allowlist is a JSON
 * array and the endpoint validates against the document_type enum.
 *
 * Factory defaults mirror the seed rows in migration 037 + 038:
 *   - Employee/client-side features all OFF except unrequested-uploads
 *     (which is ON but only reachable when `clients_can_upload_documents`
 *     is also on — i.e. the unrequested flag is a sub-toggle of the
 *     master client-uploads switch).
 *   - Allowed types: all six.
 */

const ALL_TYPES = ['vaccine_record', 'waiver', 'contract', 'form_of_id', 'health_records', 'other'];
const TYPE_LABELS = {
  vaccine_record: 'Vaccine record',
  waiver: 'Waiver',
  contract: 'Contract',
  form_of_id: 'Form of ID',
  health_records: 'Health records',
  other: 'Other',
};

const DEFAULTS = {
  employees_can_manage_client_documents: false,
  clients_can_upload_documents: false,
  client_uploads_require_approval: false,
  clients_can_upload_unrequested_documents: true,
  client_upload_allowed_types: [...ALL_TYPES],
};

const ClientDocumentsSettingsEditor = () => {
  const [settings, setSettings] = useState({ ...DEFAULTS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // Sensitive-data disclaimer gate. Client-uploaded files frequently
  // include vaccine/health records and government-issued ID, so the
  // first save attempt by an unacknowledged admin pops the modal.
  const ack = useSensitiveDataAck();
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await clientDocuments.getSettings();
      const data = res.data || {};
      setSettings({
        employees_can_manage_client_documents: !!data.employees_can_manage_client_documents,
        clients_can_upload_documents: !!data.clients_can_upload_documents,
        client_uploads_require_approval: !!data.client_uploads_require_approval,
        clients_can_upload_unrequested_documents: !!data.clients_can_upload_unrequested_documents,
        client_upload_allowed_types: Array.isArray(data.client_upload_allowed_types)
          ? data.client_upload_allowed_types
          : [...ALL_TYPES],
      });
    } catch (err) {
      console.error('Failed to load client documents settings:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const persist = async (values) => {
    await clientDocuments.updateSettings(values);
  };

  const performSave = async () => {
    setSaving(true);
    try {
      await persist(settings);
      toast.success('Client document settings saved');
    } catch (err) {
      console.error('Save client documents settings error:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!ack.loading && !ack.acknowledged) {
      setShowDisclaimer(true);
      return;
    }
    await performSave();
  };

  const handleRestoreDefaults = async () => {
    if (!window.confirm('Reset all client-document toggles to factory defaults? This only affects these settings.')) return;
    setRestoring(true);
    try {
      await persist(DEFAULTS);
      setSettings({ ...DEFAULTS, client_upload_allowed_types: [...DEFAULTS.client_upload_allowed_types] });
      toast.success('Defaults restored');
    } catch (err) {
      console.error('Restore defaults error:', err);
      toast.error('Failed to restore defaults');
    } finally {
      setRestoring(false);
    }
  };

  const toggleType = (type) => {
    setSettings((prev) => {
      const has = prev.client_upload_allowed_types.includes(type);
      const next = has
        ? prev.client_upload_allowed_types.filter((t) => t !== type)
        : [...prev.client_upload_allowed_types, type];
      return { ...prev, client_upload_allowed_types: next };
    });
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </div>
    );
  }

  // Helper to render one boolean toggle row.
  const Toggle = ({ id, checked, onChange, label, description, disabled, disabledHint }) => (
    <div className="bg-gray-50 p-4 rounded-lg">
      <div className="flex items-start space-x-3">
        <input
          type="checkbox"
          id={id}
          checked={!!checked}
          onChange={onChange}
          disabled={disabled}
          className="mt-1 h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
        />
        <div className="flex-1">
          <label htmlFor={id} className="font-semibold text-gray-900 cursor-pointer block">
            {label}
          </label>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
          {disabled && disabledHint && (
            <p className="text-xs text-yellow-600 mt-2">⚠️ {disabledHint}</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="card">
      {showDisclaimer && (
        <SensitiveDataDisclaimerModal
          featureName="client document uploads (vaccine records, health records, ID, etc.)"
          onAcknowledge={async () => {
            await ack.acknowledge();
            await performSave();
          }}
          onClose={() => setShowDisclaimer(false)}
        />
      )}
      <div className="flex items-center space-x-3 mb-6">
        <FileText className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Client Document Settings</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Control how documents (waivers, vaccine records, contracts, IDs, health records) can be
        stored on client profiles and who is allowed to upload them. The <em>Document Requests</em>{' '}
        workflow — where you ask specific clients for specific files — is always available to
        admins regardless of these toggles.
      </p>

      <div className="space-y-6">
        <Toggle
          id="doc_employees_manage"
          checked={settings.employees_can_manage_client_documents}
          onChange={(e) => setSettings({ ...settings, employees_can_manage_client_documents: e.target.checked })}
          label="Allow employees to manage client documents"
          description="When enabled, groomers can view, upload, and delete (their own uploads) on any client's profile. Admins can always manage documents regardless of this setting."
        />

        <Toggle
          id="doc_clients_upload"
          checked={settings.clients_can_upload_documents}
          onChange={(e) => setSettings({ ...settings, clients_can_upload_documents: e.target.checked })}
          label="Allow clients to upload documents"
          description="Master switch for any client upload. When disabled, clients can neither fulfill document requests nor upload free-form. Admins and employees (with the toggle above) can still upload to a client's profile."
        />

        <Toggle
          id="doc_require_approval"
          checked={settings.client_uploads_require_approval}
          onChange={(e) => setSettings({ ...settings, client_uploads_require_approval: e.target.checked })}
          disabled={!settings.clients_can_upload_documents}
          disabledHint="Client uploads are currently disabled — this only matters when clients can upload."
          label="Require admin approval on client uploads"
          description="When enabled, client-uploaded documents land with a Pending status and must be approved by an admin before being treated as official. Admin/employee uploads are always auto-approved."
        />

        <Toggle
          id="doc_allow_unrequested"
          checked={settings.clients_can_upload_unrequested_documents}
          onChange={(e) => setSettings({ ...settings, clients_can_upload_unrequested_documents: e.target.checked })}
          disabled={!settings.clients_can_upload_documents}
          disabledHint="Client uploads are currently disabled — this only matters when clients can upload."
          label="Allow clients to upload free-form (unrequested) documents"
          description="When enabled, clients can upload any allowed document type from their profile at will. When disabled, clients can only upload in response to a specific request you create for them."
        />

        {/* Per-type allowlist. Only relevant when clients can upload free-form at all. */}
        <div className={`bg-gray-50 p-4 rounded-lg ${(!settings.clients_can_upload_documents || !settings.clients_can_upload_unrequested_documents) ? 'opacity-60' : ''}`}>
          <div className="mb-3">
            <h3 className="font-semibold text-gray-900">Document types clients may upload free-form</h3>
            <p className="text-sm text-gray-600 mt-1">
              Restrict which categories clients can pick from when uploading an unrequested document.
              These restrictions do NOT apply to documents you request — you can always request any
              type from any client.
            </p>
            {(!settings.clients_can_upload_documents || !settings.clients_can_upload_unrequested_documents) && (
              <p className="text-xs text-yellow-600 mt-2">
                ⚠️ Free-form client uploads are disabled above, so this list has no effect right now.
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ALL_TYPES.map((t) => {
              const checked = settings.client_upload_allowed_types.includes(t);
              return (
                <label
                  key={t}
                  className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleType(t)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-800">{TYPE_LABELS[t]}</span>
                </label>
              );
            })}
          </div>
          {settings.client_upload_allowed_types.length === 0 && (
            <p className="text-xs text-red-600 mt-2">
              With no types selected, clients won't be able to start a free-form upload at all.
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving || restoring}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Save size={18} />
          <span>{saving ? 'Saving...' : 'Save Document Settings'}</span>
        </button>
        <button
          onClick={handleRestoreDefaults}
          disabled={saving || restoring}
          className="btn btn-secondary flex items-center space-x-2"
          title="Reset these toggles to factory defaults. Does not touch stored documents or requests."
        >
          <RotateCcw size={18} />
          <span>{restoring ? 'Restoring...' : 'Restore Defaults'}</span>
        </button>
        <p className="text-xs text-gray-500">
          Restore Defaults only resets the toggles on this card. Existing documents and requests are left alone.
        </p>
      </div>
    </div>
  );
};

export default ClientDocumentsSettingsEditor;
